import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import { useQuery } from "@tanstack/react-query";
import { s3baseUrl } from "src/config/config";
import { helpingVideoAgainstCategory } from "src/DAL/HelpingVideos/AdminHelpingVideos";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrMediaCard from "src/design-system/components/GrMediaCard";
import GrIcon from "src/design-system/components/GrIcon";
import GrCard from "src/design-system/components/GrCard";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import { isFirstRow, libraryGridSx } from "../Video/videoHelpers";

/**
 * Helping videos of one category (/helping_videos_category/helping-videos/:id): one card per
 * video; a card opens the video's page.
 */

export default function HelpingVideos() {
  const navigate = useNavigate();
  const { id } = useParams();
  // The category the member came from (the list passes it); opened by a link, the old title shows.
  const { state: category } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const { data: adminVideos = [], isLoading } = useQuery({
    queryKey: ["helpingVideos", id],
    queryFn: async () => {
      const postData = {
        search: "",
      };
      const result = await helpingVideoAgainstCategory(postData, id);
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result;
    },
    select: (result) => result?.helping_videos || [],
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const openVideo = (value) =>
    navigate(
      `/helping_videos_category/${id}/helping-videos/${value.helping_video_slug}`,
      {
        state: value,
      }
    );

  return (
    <GrPage>
      <GrPageHeader
        onBack={() => navigate(-1)}
        title={category?.title || "Helping videos"}
      />

      {isLoading ? (
        <GrLoader />
      ) : adminVideos.length > 0 ? (
        <Box sx={libraryGridSx}>
          {adminVideos.map((value, index) => {
            const thumb = value?.thumbnail_image?.thumbnail_1;
            return (
              <GrMediaCard
                eagerImage={isFirstRow(index)}
                key={value?._id || value?.helping_video_slug || index}
                image={thumb ? s3baseUrl + thumb : null}
                imageAlt={value?.title || ""}
                placeholderIcon={icons.video}
                play
                title={value?.title || ""}
                description={value?.short_description}
                expandDescription
                onClick={() => openVideo(value)}
              />
            );
          })}
        </Box>
      ) : (
        <GrCard>
          <GrEmptyState
            icon={<GrIcon icon={icons.video} />}
            title="Videos Not Found"
          />
        </GrCard>
      )}
    </GrPage>
  );
}
